import React, { useState, useEffect } from 'react';
import { useContent } from '../../hooks/useContent';
import { useConsentStatus } from '../../hooks/useConsentStatus';
import { useConsentAction } from '../../hooks/useConsentAction';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { AlertCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { SafeHtml } from '../security/SafeHtml';

/**
 * ConsentGate — Blocks children until user accepts required content.
 *
 * Props:
 *   contentKey — platform_content key
 *   flow       — flow context for consent recording
 *   country    — optional country code
 *   children   — content to render when consent is current
 *   onConsented — optional callback when consent is recorded
 *
 * Logic:
 *   IF content.requires_acceptance = false → render children
 *   IF consent status = CURRENT → render children
 *   IF NEVER_ACCEPTED | VERSION_OUTDATED | WITHDRAWN → BLOCK with modal
 *
 * Acceptance mechanisms:
 *   "passive"             → "Continue" button
 *   "checkbox"            → checkbox + continue
 *   "checkbox_signature"  → checkbox + typed name + continue
 *   "full_esign"          → checkbox + typed name + confirm
 */
export function ConsentGate({
  contentKey,
  flow,
  country,
  children,
  onConsented,
}) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const { loading: contentLoading, error: contentError, content } = useContent(contentKey, { flow, country });
  const { loading: statusLoading, status, refetch: refetchStatus } = useConsentStatus(contentKey);
  const { accept, loading: acceptLoading } = useConsentAction();

  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [acceptError, setAcceptError] = useState(null);

  // Reset form state when content changes
  useEffect(() => {
    setChecked(false);
    setTypedName('');
    setAcceptError(null);
  }, [contentKey]);

  const isLoading = contentLoading || statusLoading;

  // Loading state
  if (isLoading) {
    return (
      <div data-testid={`consent-gate-loading-${contentKey}`} className="space-y-3 p-4">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  // Content error — show error, don't block silently
  if (contentError || !content) {
    return (
      <div
        className="flex items-center gap-2 p-4 rounded-md bg-destructive/10 text-destructive text-sm"
        data-testid={`consent-gate-error-${contentKey}`}
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Required legal content unavailable ({contentKey}). Please try again later.</span>
      </div>
    );
  }

  // No acceptance required → pass through
  if (!content.requiresAcceptance) {
    return <>{children}</>;
  }

  // User not authenticated → pass through (consent tracked on auth flows)
  if (status === null) {
    return <>{children}</>;
  }

  // Consent is current → pass through
  if (status === 'CURRENT') {
    return <>{children}</>;
  }

  // Needs action: NEVER_ACCEPTED, VERSION_OUTDATED, or WITHDRAWN → BLOCK
  const mechanism = content.acceptanceMechanism || 'passive';
  const needsCheckbox = mechanism === 'checkbox' || mechanism === 'checkbox_signature' || mechanism === 'full_esign';
  const needsSignature = mechanism === 'checkbox_signature' || mechanism === 'full_esign';

  // CONSENT-INTERCEPTOR-REMEDIATION-01 (Owner decision Q4) — the legal
  // acceptance statement is the published, locale-specific
  // `acceptance_label` of this document version. A document `summary` is
  // informational and must NEVER stand in for it, and generic wording must
  // never be invented. When a checkbox acceptance is required but no
  // canonical label is published, report the configuration discrepancy
  // instead of capturing an acceptance against wording TreatX never approved.
  const acceptanceLabel = (content.acceptanceLabel || '').trim();
  const labelMissing = needsCheckbox && !acceptanceLabel;

  const canSubmit = (() => {
    if (acceptLoading) return false;
    if (labelMissing) return false;
    if (needsCheckbox && !checked) return false;
    if (needsSignature && typedName.trim().length < 2) return false;
    return true;
  })();

  const handleAccept = async () => {
    setAcceptError(null);
    try {
      await accept({
        contentKey: content.contentKey,
        contentVersion: content.version,
        acceptanceMechanism: mechanism,
        typedSignature: needsSignature ? typedName.trim() : null,
        flow,
        language,
        countryContext: country,
      });
      await refetchStatus();
      onConsented?.({ typedSignature: needsSignature ? typedName.trim() : null, contentKey: content.contentKey, version: content.version });
    } catch (err) {
      setAcceptError(err.message);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid={`consent-gate-modal-${contentKey}`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid={`consent-gate-title-${contentKey}`}>
            <ShieldCheck className="h-5 w-5 text-primary" />
            {content.title}
          </DialogTitle>
          {status === 'VERSION_OUTDATED' && (
            <p className="text-xs text-amber-600 mt-1" data-testid="consent-gate-outdated-notice">
              This document has been updated since you last accepted. Please review and accept the new version.
            </p>
          )}
        </DialogHeader>

        {/* Document body */}
        <SafeHtml
          html={content.body}
          className="prose prose-sm max-w-none dark:prose-invert my-4 px-1"
          testId={`consent-gate-body-${contentKey}`}
        />

        {/* Acceptance form */}
        <div className="space-y-4 border-t pt-4">
          {labelMissing && (
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm"
              data-testid={`consent-gate-config-error-${contentKey}`}
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {t(
                  'legal.acceptanceLabelUnavailable',
                  'This document requires a recorded acceptance, but no published acceptance statement is configured for it. Acceptance cannot be recorded until the canonical acceptance label is published.'
                )}
                {' '}
                <span className="font-mono">
                  {contentKey} v{content.version}
                </span>
              </span>
            </div>
          )}

          {needsCheckbox && !labelMissing && (
            <label className="flex items-start gap-3 cursor-pointer" data-testid={`consent-gate-checkbox-${contentKey}`}>
              <Checkbox
                checked={checked}
                onCheckedChange={setChecked}
                className="mt-0.5"
              />
              <span className="text-sm leading-snug" data-testid={`consent-gate-acceptance-label-${contentKey}`}>
                {acceptanceLabel}
              </span>
            </label>
          )}

          {needsSignature && (
            <div className="space-y-2" data-testid={`consent-gate-signature-${contentKey}`}>
              <label className="text-sm font-medium">Type your full name to sign</label>
              <Input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Full name"
                className="max-w-xs"
                data-testid={`consent-gate-signature-input-${contentKey}`}
              />
            </div>
          )}

          {acceptError && (
            <p className="text-sm text-destructive flex items-center gap-1.5" data-testid="consent-gate-accept-error">
              <AlertCircle className="h-3.5 w-3.5" />
              {acceptError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!canSubmit}
            className="w-full sm:w-auto"
            data-testid={`consent-gate-accept-btn-${contentKey}`}
          >
            {acceptLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : mechanism === 'full_esign' ? (
              'Confirm & Sign'
            ) : (
              'Accept & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
